import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Avatar,
  Stack,
  Fade,
  Zoom,
  Divider,
  alpha,
  useTheme
  // useMediaQuery,
} from '@mui/material';
import {
  Add,
  Security,
  Edit,
  Delete,
  ExpandMore,
  Warning,
  CheckCircle,
  Cancel,
  Info,
  Shield,
  // BugReport,
  Block,
  FileUpload,
  History,
  Gavel,
  Flag,
  NotificationImportant,
  Description,
  VerifiedUser,
  Assessment,
  Settings
  // Code,
  // Lock,
  // Refresh,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { SecurityRule } from '../types/api';
import DocumentUpload from '../components/DocumentUpload';
import DocumentProcessingHistory from '../components/DocumentProcessingHistory';

// Remove local interface - use the one from api.ts instead

interface ComplianceFramework {
  name: string;
  display_name: string;
  version: string;
  regulatory_authority: string;
  jurisdiction: string;
  security_rules: Array<{
    id: string;
    name: string;
    description: string;
    pattern: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    action: 'block' | 'flag' | 'alert' | 'log';
    confidence_threshold: number;
  }>;
}

const SecurityRules: React.FC = () => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [rules, setRules] = useState<SecurityRule[]>([]);
  const [totalRulesCount, setTotalRulesCount] = useState(0);
  const [complianceFrameworks, setComplianceFrameworks] = useState<ComplianceFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [complianceLoading, setComplianceLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [documentUploadOpen, setDocumentUploadOpen] = useState(false);
  const [processingHistoryOpen, setProcessingHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFramework, setExpandedFramework] = useState<string | false>(false);
  const [editingRule, setEditingRule] = useState<SecurityRule | null>(null);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    direction: 'inbound' as SecurityRule['direction'],
    rule_type: 'keyword' as SecurityRule['rule_type'],
    pattern: '',
    action: 'flag' as SecurityRule['action'],
    severity: 'medium' as SecurityRule['severity'],
    category: 'custom' as SecurityRule['category'],
    confidence_threshold: 0.5,
    response_action: {
      replacement_text: '',
      notify_admin: false,
      log_level: 'info' as 'info' | 'warn' | 'error'
    },
    tags: ''
  });

  useEffect(() => {
    fetchRules();
    fetchComplianceFrameworks();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);

      const isAdminOrAbove = user?.role === 'admin' || user?.role === 'super_admin';
      const endpoint = isAdminOrAbove ? '/api/admin/security-rules' : '/api/security-rules';
      const response = await axios.get(endpoint, { params: { limit: 1000 } });

      const fetchedRules = response.data.rules || [];
      setRules(fetchedRules);
      // Use pagination.total if available (accurate even with pagination)
      setTotalRulesCount(response.data.pagination?.total ?? fetchedRules.length);
    } catch (error: any) {
      console.error('Failed to fetch security rules:', error);

      if (error.response?.status === 403) {
        setError('You need admin privileges to manage security rules');
      } else if (error.response?.status === 404) {
        setError(null);
        setRules([]);
        setTotalRulesCount(0);
      } else {
        setError('Failed to load security rules');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchComplianceFrameworks = async () => {
    try {
      setComplianceLoading(true);
      const response = await axios.get('/api/compliance/frameworks');
      setComplianceFrameworks(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch compliance frameworks:', error);
      // Don't show error, just leave empty
      setComplianceFrameworks([]);
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleCreateRule = async () => {
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map((tag) => tag.trim()) : []
      };

      if (editingRule) {
        // Update existing rule
        await axios.put(`/api/admin/security-rules/${editingRule.id}`, payload);
        toast.success('Security rule updated successfully!');
      } else {
        // Create new rule
        await axios.post('/api/admin/security-rules', payload);
        toast.success('Security rule created successfully!');
      }

      setCreateDialogOpen(false);
      setEditingRule(null);
      fetchRules();
      resetForm();
    } catch (error: any) {
      console.error('Failed to save security rule:', error);
      toast.error(error.response?.data?.error || 'Failed to save security rule');
    }
  };

  const handleEditRule = (rule: SecurityRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      direction: rule.direction,
      rule_type: rule.rule_type,
      pattern: rule.pattern,
      action: rule.action,
      severity: rule.severity,
      category: rule.category || 'custom',
      confidence_threshold: rule.confidence_threshold || 0.5,
      response_action: {
        replacement_text: rule.response_action?.replacement_text || '',
        notify_admin: rule.response_action?.notify_admin || false,
        log_level: rule.response_action?.log_level || 'info'
      },
      tags: Array.isArray(rule.tags) ? rule.tags.join(', ') : ''
    });
    setCreateDialogOpen(true);
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this security rule?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/security-rules/${ruleId}`);
      fetchRules();
      toast.success('Security rule deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete security rule:', error);
      toast.error(error.response?.data?.error || 'Failed to delete security rule');
    }
  };

  const resetForm = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      direction: 'inbound',
      rule_type: 'keyword',
      pattern: '',
      action: 'flag',
      severity: 'medium',
      category: 'custom',
      confidence_threshold: 0.5,
      response_action: {
        replacement_text: '',
        notify_admin: false,
        log_level: 'info'
      },
      tags: ''
    });
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'info';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'block':
        return <Block />;
      case 'flag':
        return <Warning />;
      case 'redact':
        return <Edit />;
      case 'modify':
        return <Edit />;
      case 'alert':
        return <Info />;
      case 'log':
        return <Description />;
      default:
        return <Security />;
    }
  };

  const getComplianceActionIcon = (action: string) => {
    switch (action) {
      case 'block':
        return <Block fontSize="small" />;
      case 'flag':
        return <Flag fontSize="small" />;
      case 'alert':
        return <NotificationImportant fontSize="small" />;
      case 'log':
        return <Description fontSize="small" />;
      default:
        return <Security fontSize="small" />;
    }
  };

  const getComplianceActionColor = (action: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (action) {
      case 'block':
        return 'error';
      case 'flag':
        return 'warning';
      case 'alert':
        return 'info';
      case 'log':
        return 'success';
      default:
        return 'info';
    }
  };

  const handleAccordionChange =
    (frameworkName: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFramework(isExpanded ? frameworkName : false);
    };

  const totalComplianceRules = Array.isArray(complianceFrameworks)
    ? complianceFrameworks.reduce((sum, fw) => {
        const rules = fw.security_rules;
        if (!rules) return sum;
        return sum + (Array.isArray(rules) ? rules.length : Object.keys(rules).length);
      }, 0)
    : 0;

  const defaultRulesInfo = [
    {
      name: 'Prompt Injection Detection',
      description: 'Detects attempts to manipulate AI behavior through prompt injection techniques',
      category: 'injection',
      severity: 'high',
      status: 'Built-in'
    },
    {
      name: 'PII Detection',
      description: 'Identifies and protects personally identifiable information in prompts',
      category: 'pii',
      severity: 'high',
      status: 'Built-in'
    },
    {
      name: 'Jailbreak Prevention',
      description: 'Prevents attempts to bypass AI safety measures',
      category: 'jailbreak',
      severity: 'critical',
      status: 'Built-in'
    },
    {
      name: 'Hate Speech Detection',
      description: 'Detects discriminatory language, racial slurs, and hate-based content',
      category: 'hate_speech',
      severity: 'high',
      status: 'Built-in'
    },
    {
      name: 'Sexual Content Filter',
      description: 'Blocks explicit sexual content and inappropriate adult material',
      category: 'sexual_content',
      severity: 'high',
      status: 'Built-in'
    },
    {
      name: 'Violence Detection',
      description: 'Identifies violent threats, self-harm content, and harmful instructions',
      category: 'violence',
      severity: 'critical',
      status: 'Built-in'
    },
    {
      name: 'Legacy Toxicity Filter',
      description: 'General toxic content detection (includes combined moderation rules)',
      category: 'toxic',
      severity: 'medium',
      status: 'Built-in'
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Calculate summary statistics from actual DB data
  const activeRules = rules.filter((r) => r.is_active);
  const inactiveRules = rules.filter((r) => !r.is_active);
  const summaryStats = {
    total: totalRulesCount,
    active: activeRules.length,
    inactive: inactiveRules.length,
    compliance: totalComplianceRules,
    // custom = user-created rules (have created_by set)
    custom: rules.filter((r) => r.created_by != null).length,
    builtIn: defaultRulesInfo.length
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Enhanced Header Section */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: 'primary.main',
                border: `3px solid ${theme.palette.primary.main}`
              }}
            >
              <Shield sx={{ fontSize: 36 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '1.75rem', sm: '2.5rem' },
                  mb: 1
                }}
              >
                Security Rules
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                Define and manage security rules to detect and handle potential threats
              </Typography>
            </Box>
          </Stack>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Tooltip title="View processing history">
                <IconButton
                  onClick={() => setProcessingHistoryOpen(true)}
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                  }}
                >
                  <History />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<FileUpload />}
                onClick={() => setDocumentUploadOpen(true)}
                sx={{
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Upload Document
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                  }
                }}
              >
                Add Custom Rule
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Summary Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: 'Total Rules',
            value: summaryStats.total,
            icon: <Assessment />,
            color: 'primary' as const
          },
          {
            label: 'Active Rules',
            value: summaryStats.active,
            icon: <CheckCircle />,
            color: 'success' as const
          },
          {
            label: 'Inactive Rules',
            value: summaryStats.inactive,
            icon: <Shield />,
            color: 'warning' as const
          },
          {
            label: 'Compliance Rules',
            value: summaryStats.compliance,
            icon: <Gavel />,
            color: 'info' as const
          },
          {
            label: 'Custom Rules',
            value: summaryStats.custom,
            icon: <Settings />,
            color: 'secondary' as const
          }
        ].map((stat, index) => (
          <Grid item xs={6} sm={6} md={2.4} key={index}>
            <Zoom in timeout={300 + index * 100}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                  border: `2px solid ${alpha(theme.palette[stat.color].main, 0.3)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette[stat.color].main, 0.05)} 0%, ${alpha(theme.palette[stat.color].main, 0.12)} 100%)`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 16px ${alpha(theme.palette[stat.color].main, 0.2)}`
                  }
                }}
              >
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ color: `${stat.color}.main`, mb: 1 }}>{stat.icon}</Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: `${stat.color}.main`, mb: 0.5 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {error && (
        <Fade in>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      )}

      {/* Built-in Rules Section */}
      <Fade in timeout={500}>
        <Card
          elevation={2}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.15)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.success.main, 0.3)}`
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: 'success.main',
                  border: `2px solid ${theme.palette.success.main}`
                }}
              >
                <Shield sx={{ fontSize: 28 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Built-in Security Rules
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Core security rules that are always active to protect against common threats
                </Typography>
              </Box>
              <Chip
                label={`${defaultRulesInfo.length} Rules`}
                color="success"
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {defaultRulesInfo.map((rule, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Zoom in timeout={300 + index * 50}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: 2,
                        border: `2px solid ${alpha(theme.palette[getSeverityColor(rule.severity)].main, 0.3)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette[getSeverityColor(rule.severity)].main, 0.05)} 0%, ${alpha(theme.palette[getSeverityColor(rule.severity)].main, 0.1)} 100%)`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(theme.palette[getSeverityColor(rule.severity)].main, 0.2)}`
                        }
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ mb: 1.5 }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                          {rule.name}
                        </Typography>
                        <Chip
                          label={rule.status}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, minHeight: 40 }}
                      >
                        {rule.description}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={rule.severity.toUpperCase()}
                          size="small"
                          color={getSeverityColor(rule.severity)}
                          sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={rule.category.toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </Stack>
                    </Paper>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      {/* Compliance Framework Security Rules Section */}
      <Fade in timeout={600}>
        <Card
          elevation={2}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.15)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.info.main, 0.3)}`
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: alpha(theme.palette.info.main, 0.15),
                    color: 'info.main',
                    border: `2px solid ${theme.palette.info.main}`
                  }}
                >
                  <Gavel sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Compliance Framework Security Rules
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Security rules defined by compliance frameworks (HIPAA, GDPR, SOC 2, ISO 27001,
                    NIST 800-53)
                  </Typography>
                </Box>
              </Stack>
              <Chip
                label={`${totalComplianceRules} Total Rules`}
                color="info"
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {complianceLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress />
              </Box>
            ) : !Array.isArray(complianceFrameworks) || complianceFrameworks.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No compliance frameworks are currently enabled. Enable frameworks in Organization
                Settings to see their security rules.
              </Alert>
            ) : (
              complianceFrameworks.map((framework, index) => (
                <Zoom in timeout={300 + index * 100} key={framework.name}>
                  <Accordion
                    expanded={expandedFramework === framework.name}
                    onChange={handleAccordionChange(framework.name)}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      '&:before': { display: 'none' },
                      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        bgcolor: alpha(theme.palette.info.main, 0.03),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.info.main, 0.06)
                        }
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        sx={{ pr: 2 }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: alpha(theme.palette.info.main, 0.15),
                              color: 'info.main'
                            }}
                          >
                            <VerifiedUser />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {framework.display_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {framework.regulatory_authority} • {framework.jurisdiction} • v
                              {framework.version}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip
                          label={`${framework.security_rules?.length || 0} Rules`}
                          size="small"
                          color="info"
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {framework.security_rules && framework.security_rules.length > 0 ? (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow
                                sx={{
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
                                  '& th': {
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5
                                  }
                                }}
                              >
                                <TableCell>Rule Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="center">Severity</TableCell>
                                <TableCell align="center">Action</TableCell>
                                <TableCell align="center">Threshold</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {framework.security_rules.map((rule, ruleIndex) => (
                                <Fade in timeout={200 + ruleIndex * 50} key={rule.id}>
                                  <TableRow
                                    hover
                                    sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.info.main, 0.04)
                                      }
                                    }}
                                  >
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {rule.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {rule.id}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{rule.description}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        label={rule.severity.toUpperCase()}
                                        size="small"
                                        color={getSeverityColor(rule.severity)}
                                        sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        icon={getComplianceActionIcon(rule.action)}
                                        label={rule.action.toUpperCase()}
                                        size="small"
                                        color={getComplianceActionColor(rule.action)}
                                        variant="outlined"
                                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {(rule.confidence_threshold * 100).toFixed(0)}%
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                </Fade>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box sx={{ p: 2 }}>
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No security rules defined for this framework.
                          </Alert>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Zoom>
              ))
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Custom Rules Section */}
      <Fade in timeout={700}>
        <Card
          elevation={2}
          sx={{
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.15)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  color: 'warning.main',
                  border: `2px solid ${theme.palette.warning.main}`
                }}
              >
                <Settings sx={{ fontSize: 28 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Custom Security Rules
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Organization-specific rules tailored to your security requirements
                </Typography>
              </Box>
              {rules.length > 0 && (
                <Chip label={`${rules.length} Rules`} color="warning" sx={{ fontWeight: 700 }} />
              )}
            </Stack>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {user?.role !== 'admin' && user?.role !== 'super_admin' ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Custom security rules can only be managed by administrators. Contact your admin to
                create custom rules for your specific use case.
              </Alert>
            ) : rules.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: alpha(theme.palette.background.default, 0.5)
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    margin: '0 auto',
                    mb: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: 'warning.main'
                  }}
                >
                  <Settings sx={{ fontSize: 48 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  No Custom Rules Configured
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Create custom security rules to detect specific patterns or threats relevant to
                  your use case.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    mt: 1,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}
                >
                  Create First Rule
                </Button>
              </Paper>
            ) : (
              <List sx={{ p: 0 }}>
                {rules.map((rule, index) => (
                  <Fade in timeout={200 + index * 50} key={rule.id}>
                    <ListItem
                      sx={{
                        mb: 1,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.warning.main, 0.04),
                          borderColor: alpha(theme.palette.warning.main, 0.3)
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: alpha(
                              theme.palette[getSeverityColor(rule.severity)].main,
                              0.15
                            ),
                            color: `${getSeverityColor(rule.severity)}.main`
                          }}
                        >
                          {getActionIcon(rule.action)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {rule.name}
                            </Typography>
                            <Chip
                              label={rule.severity.toUpperCase()}
                              size="small"
                              color={getSeverityColor(rule.severity)}
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            />
                            {rule.is_active ? (
                              <Chip
                                label="ACTIVE"
                                size="small"
                                color="success"
                                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            ) : (
                              <Chip
                                label="INACTIVE"
                                size="small"
                                variant="outlined"
                                icon={<Cancel sx={{ fontSize: 14 }} />}
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              />
                            )}
                            {rule.source_document && (
                              <Chip
                                icon={<Description fontSize="small" />}
                                label="From Document"
                                size="small"
                                variant="outlined"
                                color="info"
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                              {rule.description || 'No description'}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Chip
                                label={`Direction: ${rule.direction || 'inbound'}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={`Type: ${rule.rule_type}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={`Action: ${rule.action}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={`Matches: ${rule.match_count}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Stack>
                            {rule.source_document && (
                              <Typography
                                variant="caption"
                                color="info.main"
                                display="block"
                                sx={{ mt: 0.5 }}
                              >
                                📄 Source: {rule.source_document} • Processed:{' '}
                                {new Date(
                                  rule.processing_timestamp || rule.created_at
                                ).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit Rule" arrow>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditRule(rule)}
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Rule" arrow>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteRule(rule.id)}
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.2),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Fade>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Create Rule Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: 'primary.main'
              }}
            >
              {editingRule ? <Edit /> : <Add />}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editingRule ? 'Edit Security Rule' : 'Create Custom Security Rule'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '32px !important' }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={formData.direction}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      direction: e.target.value as SecurityRule['direction']
                    })
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="inbound">Inbound (Filter Prompts)</MenuItem>
                  <MenuItem value="outbound">Outbound (Filter Responses)</MenuItem>
                  <MenuItem value="both">Both Directions</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Rule Type</InputLabel>
                <Select
                  value={formData.rule_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      rule_type: e.target.value as SecurityRule['rule_type']
                    })
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="keyword">Keyword</MenuItem>
                  <MenuItem value="regex">Regular Expression</MenuItem>
                  <MenuItem value="pattern_match">Pattern Match</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={formData.action}
                  onChange={(e) =>
                    setFormData({ ...formData, action: e.target.value as SecurityRule['action'] })
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="flag">Flag</MenuItem>
                  <MenuItem value="block">Block</MenuItem>
                  <MenuItem value="redact">Redact</MenuItem>
                  <MenuItem value="modify">Modify</MenuItem>
                  <MenuItem value="alert">Alert</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={formData.severity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      severity: e.target.value as SecurityRule['severity']
                    })
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: e.target.value as SecurityRule['category']
                    })
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="custom">Custom</MenuItem>
                  <MenuItem value="injection">Injection</MenuItem>
                  <MenuItem value="pii">PII</MenuItem>
                  <MenuItem value="jailbreak">Jailbreak</MenuItem>
                  <MenuItem value="hate_speech">Hate Speech</MenuItem>
                  <MenuItem value="sexual_content">Sexual Content</MenuItem>
                  <MenuItem value="violence">Violence</MenuItem>
                  <MenuItem value="toxic">Toxic (Legacy)</MenuItem>
                  <MenuItem value="data_extraction">Data Extraction</MenuItem>
                  <MenuItem value="data_leakage">Data Leakage</MenuItem>
                  <MenuItem value="proprietary_info">Proprietary Info</MenuItem>
                  <MenuItem value="credentials">Credentials</MenuItem>
                  <MenuItem value="api_exposure">API Exposure</MenuItem>
                  <MenuItem value="code_exposure">Code Exposure</MenuItem>
                  <MenuItem value="system_info">System Info</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pattern"
                value={formData.pattern}
                onChange={(e) => setFormData({ ...formData, pattern: e.target.value })}
                multiline
                rows={3}
                helperText="Enter the pattern to match (keywords, regex, etc.)"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                helperText="Optional tags for organizing rules"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            {/* Response Action Configuration for Outbound Rules */}
            {(formData.direction === 'outbound' || formData.direction === 'both') && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Settings color="primary" />
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                      Response Action Configuration
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="textSecondary">
                    Configure how responses should be handled when this rule is triggered
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Replacement Text"
                    value={formData.response_action?.replacement_text || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        response_action: {
                          ...formData.response_action,
                          replacement_text: e.target.value
                        }
                      })
                    }
                    helperText="Text to use when redacting content (optional)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Log Level</InputLabel>
                    <Select
                      value={formData.response_action?.log_level || 'info'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          response_action: {
                            ...formData.response_action,
                            log_level: e.target.value as 'info' | 'warn' | 'error'
                          }
                        })
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="info">Info</MenuItem>
                      <MenuItem value="warn">Warning</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <input
                        type="checkbox"
                        id="notify-admin"
                        checked={formData.response_action?.notify_admin || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            response_action: {
                              ...formData.response_action,
                              notify_admin: e.target.checked
                            }
                          })
                        }
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                      />
                      <label htmlFor="notify-admin" style={{ cursor: 'pointer', fontWeight: 500 }}>
                        Notify administrators when this rule is triggered
                      </label>
                    </Stack>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.default, 0.5)
          }}
        >
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              resetForm();
            }}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateRule}
            variant="contained"
            disabled={!formData.name || !formData.pattern}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            {editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <DocumentUpload
        open={documentUploadOpen}
        onClose={() => setDocumentUploadOpen(false)}
        onSuccess={() => {
          setDocumentUploadOpen(false);
          fetchRules(); // Refresh rules after successful upload
        }}
      />

      {/* Document Processing History Dialog */}
      <DocumentProcessingHistory
        open={processingHistoryOpen}
        onClose={() => setProcessingHistoryOpen(false)}
      />
    </Container>
  );
};

export default SecurityRules;
